import type { NetworkError } from "@verbleif/shared/src/composables/Api/Error/NetworkError";
import type { ServerError } from "@verbleif/shared/src/composables/Api/Error/ServerError";

import type { AuthenticateOptions } from "@verbleif/shared/src/composables/Api/Plugin/Auth/AuthPlugin";

import { HttpStatusCode } from "@axios-extensions/plugins-api";
import { onAuthenticateSuccess, storeTokens } from "@cms/core/onAuthenticateSuccess";
import { useLogout } from "@cms/features/Auth/Logout/useLogout";
import { api, installInterceptor } from "@verbleif/shared/src/composables/Api/api";
import { ClientError } from "@verbleif/shared/src/composables/Api/Error/ClientError";
import { AuthPath, createAuthenticatePlugin } from "@verbleif/shared/src/composables/Api/Plugin/Auth/AuthPlugin";
import { ACCESS_TOKEN_REVOKE_PATH } from "@verbleif/shared/src/composables/Api/Plugin/Auth/revokeAccessToken";
import { RefreshTokenResponseInterceptor } from "@verbleif/shared/src/composables/Api/Plugin/RefreshToken/RefreshTokenResponseInterceptor";
import { attemptDevSilentReauthorize } from "@verbleif/shared/src/composables/AuthStore/devSilentReauthorize";
import { useAuthStore } from "@verbleif/shared/src/composables/AuthStore/useAuthStore";
import { useConfigStore } from "@verbleif/shared/src/composables/ConfigStore/useConfigStore";

const { authenticate } = createAuthenticatePlugin({
  axios: api,
  path: AuthPath.OAUTH_REFRESH_V2,
  storeTokens,
  onAuthenticateSuccess,
  onAuthenticateFailed,
});

async function onAuthenticateFailed(
  e: ClientError | ServerError | NetworkError,
) {
  if (e instanceof ClientError && e.status === HttpStatusCode.BAD_REQUEST && e.response?.data?.error === "invalid_grant") {
    if (attemptDevSilentReauthorize()) {
      console.log("[Refresh] Invalid grant — attempting silent re-authorize");
      return;
    }

    console.log("[Refresh] Invalid grant, redirecting to login");
    const { logout } = useLogout();
    return logout({ logoutAuthServer: false });
  }
}

export async function refreshToken(options: AuthenticateOptions = {}) {
  console.log("Refreshing token");
  const authStore = useAuthStore();
  const { config } = useConfigStore();

  if (!authStore.getRefreshToken.value) {
    console.error("No refresh token set.");
    throw new Error("No refresh token set.");
  }
  if (!config.value) {
    console.error("Config not set");
    throw new Error("Config not set");
  }
  await authenticate({
    grant_type: "refresh_token",
    refresh_token: authStore.getRefreshToken.value,
    client_id: `${config.value.loginClientId}`, // Replace with your client ID.
    redirect_uri: `${config.value.loginRedirect}/oauth/v2/callback`, // Replace with your callback URL.
  }, options);
}

const refreshTokenInterceptorPlugin = RefreshTokenResponseInterceptor(
  api,
  // A request that got a 401 only needs the new tokens to retry. Waiting for the whole session load would hang
  // when that load joins this same request or gets a 401 of its own: it would wait on the refresh it is part of.
  () => refreshToken({ waitFor: "tokens" }),
  // A 401 on the revoke call means the session is already gone; refreshing would mint a fresh
  // token mid-logout (or fail and start a second logout).
  ["/oauth/v2/token", ACCESS_TOKEN_REVOKE_PATH],
);
installInterceptor(refreshTokenInterceptorPlugin);
